/**
 * Painel da conferência quando a empresa usa volumes pela caixa do transportador.
 * Um bip vale 1. Quantidade só se o conferente informar mais de um.
 * Peso acima do máximo só avisa.
 */
import api from '../api.js';
import { notify } from '../../versa/msgbox.js';

const esc = (value) => String(value ?? '')
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;');

function isoToBr(iso) {
	const text = String(iso || '').slice(0, 10);
	const parts = text.split('-');
	if (parts.length !== 3)
		return '';
	return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function applyLines(node, state) {
	const totals = {};
	for (const volume of state.volumes || []) {
		for (const item of volume.items || []) {
			const number = item.output_item_number;
			totals[number] = (totals[number] || 0) + Number(item.quantity || 0);
		}
	}
	node.querySelectorAll('tr[data-number]:not(.conf-div-row)').forEach((row) => {
		const number = Number(row.dataset.number);
		const qtyInput = row.querySelector('.conf-qty');
		if (!qtyInput)
			return;
		const units = totals[number] || 0;
		const factor = Number(qtyInput.dataset.factor) || 1;
		const packUi = qtyInput.dataset.packUi === '1' && factor > 1 && units % factor === 0;
		qtyInput.value = units ? String(packUi ? units / factor : units) : '';
		if (units && !packUi)
			qtyInput.dataset.packUi = '0';
		const required = parseInt(qtyInput.dataset.required, 10);
		const shown = parseInt(qtyInput.value, 10);
		const diffCell = row.querySelector('.conf-diff');
		if (diffCell && qtyInput.value !== '' && !Number.isNaN(shown)) {
			const diff = shown - required;
			diffCell.textContent = diff === 0 ? '✓' : String(diff);
			diffCell.style.color = diff === 0 ? '#2a7' : '#c80';
			row.querySelector('.conf-div-row');
			const divRow = node.querySelector(`.conf-div-row[data-number="${number}"]`);
			if (divRow)
				divRow.style.display = diff < 0 ? '' : 'none';
		}
		if (units) {
			const lot = row.querySelector('.conf-lot');
			const exp = row.querySelector('.conf-expiry');
			if (lot && !lot.disabled && !lot.value)
				lot.value = lot.dataset.expectedLot || '';
			if (exp && !exp.disabled && !exp.value)
				exp.value = isoToBr(exp.dataset.expectedExpiry || '');
		}
	});
	const volInput = node.querySelector('#conf-volumes');
	if (volInput)
		volInput.value = state.closed_count ? String(state.closed_count) : '';
}

function render(node, state, carriers) {
	const host = node.querySelector('#conf-carrier-volumes');
	if (!host)
		return;
	const openVolume = (state.volumes || []).find((volume) => volume.status === 10);
	const carrierOptions = (carriers || [])
		.map((carrier) => `<option value="${esc(carrier.id)}">${esc(carrier.nick)}</option>`)
		.join('');
	const boxOptions = (state.boxes || [])
		.map((box) => `<option value="${esc(box.id)}">${esc(box.code)} · ${esc(box.length_cm)}×${esc(box.width_cm)}×${esc(box.depth_cm)} cm · ${esc(box.volume_cm3)} cm³ · máx ${esc(box.max_weight_kg)} kg</option>`)
		.join('');
	const volumesHtml = (state.volumes || []).map((volume) => {
		const items = (volume.items || []).map((item) =>
			`<li>${esc(item.sku)} ${esc(item.product_name)} — ${esc(item.quantity)}</li>`
		).join('') || '<li>Sem itens</li>';
		const alert = volume.weight_alert
			? `<div style="color:#9a3412;font-weight:600">Peso acima do máximo da caixa (${esc(volume.weight_kg)} kg / ${esc(volume.max_weight_kg)} kg). O volume pode ser fechado.</div>`
			: (volume.weight_incomplete ? '<div style="color:#92400e">Peso incompleto: algum produto não tem peso cadastrado.</div>' : '');
		const action = volume.status === 10
			? `<button type="button" class="button cv-close" data-id="${esc(volume.id)}">Fechar volume</button>`
			: `<button type="button" class="button cv-reopen" data-id="${esc(volume.id)}">Reabrir</button>`;
		return `<div style="border-top:1px solid #d6e4f5;padding:6px 0">
			<strong>Volume ${esc(volume.volume_no)}</strong> — ${esc(volume.code)}
			<span style="color:#666">(${volume.status === 10 ? 'aberto' : 'fechado'})</span>
			${action}
			${alert}
			<ul style="margin:4px 0 0 1rem">${items}</ul>
		</div>`;
	}).join('');
	const weightBanner = openVolume && openVolume.weight_alert
		? `<div style="background:#fff7ed;border:1px solid #fdba74;padding:6px 8px;margin-top:6px">Peso acima do máximo da caixa. Isso não impede o bip nem o fechamento.</div>`
		: '';
	host.innerHTML = `
		<div style="background:#f4fbf6;border:1px solid #b7e0c2;border-radius:4px;padding:8px 10px;margin-bottom:0.75rem">
			<div style="font-weight:600;margin-bottom:4px">Volumes da caixa do transportador</div>
			${state.needs_carrier ? `
				<div style="background:#fff7ed;border:1px solid #fdba74;padding:6px 8px;margin-bottom:6px">Selecione o transportador do pedido para abrir o volume.</div>
				<label>Transportador
					<select id="cv-carrier">${carrierOptions}</select>
				</label>
				<button type="button" class="button cv-save-carrier">Gravar transportador</button>
			` : `<div style="margin-bottom:6px">Transportador: <strong>${esc(state.carrier_nick || state.carrier_id)}</strong></div>`}
			${!state.needs_carrier && !openVolume ? `
				<label>Caixa
					<select id="cv-box"><option value="">(selecione)</option>${boxOptions}</select>
				</label>
				<button type="button" class="button cv-open">Abrir volume</button>
				${state.ask_next ? '<div style="color:#1d4ed8;margin-top:4px">Ainda há itens. Escolha a caixa do próximo volume.</div>' : ''}
			` : ''}
			${openVolume ? `
				<div style="margin-top:6px">Volume ${esc(openVolume.volume_no)} aberto na caixa <strong>${esc(openVolume.code)}</strong>.</div>
				<label>Produto
					<input id="cv-barcode" type="text" autocomplete="off" style="width:12rem" placeholder="Bipe">
				</label>
				<label>Quantidade
					<input id="cv-qty" type="number" min="1" step="1" style="width:5rem" placeholder="1" title="Vazio vale 1">
				</label>
				<button type="button" class="button cv-add">Incluir</button>
				${weightBanner}
			` : ''}
			${volumesHtml}
		</div>`;
}

/**
 * @param {HTMLElement} node
 * @param {{ companyId: string, outputId: number|string, output: object }} ctx
 */
export async function bindCarrierVolumePanel(node, ctx) {
	const state = { volumes: [], boxes: [], needs_carrier: !ctx.output?.carrier_id, carrier_id: ctx.output?.carrier_id, carrier_nick: '', ask_next: false, closed_count: 0 };
	let carriers = [];
	node.querySelectorAll('.conf-qty').forEach((input) => {
		input.readOnly = true;
		input.title = 'A quantidade vem dos volumes da caixa do transportador';
		input.style.background = '#f0f7ff';
	});
	const finishButton = node.querySelector('.bn_Y');
	if (finishButton && Number(ctx.output?.status) >= 60)
		finishButton.disabled = true;

	async function loadBoxes() {
		if (!state.carrier_id) {
			state.boxes = [];
			return;
		}
		const rows = await api.call(api.list_carrier_boxes_by_carrier, ctx.companyId, state.carrier_id);
		state.boxes = Array.isArray(rows) ? rows : [];
	}

	async function refresh() {
		const data = await api.call(api.list_output_carrier_volumes, ctx.companyId, ctx.outputId);
		if (!data)
			return;
		state.needs_carrier = !!data.needs_carrier;
		state.carrier_id = data.carrier_id;
		state.carrier_nick = data.carrier_nick;
		state.volumes = data.volumes || [];
		state.closed_count = data.closed_count || 0;
		state.remaining = data.remaining;
		await loadBoxes();
		if (state.needs_carrier && !carriers.length) {
			const rows = await api.call(api.list_carriers, '');
			carriers = (Array.isArray(rows) ? rows : []).filter((row) => row.company_id === ctx.companyId && !row.is_disabled);
		}
		render(node, state, carriers);
		applyLines(node, state);
		wire();
	}

	async function finishIfDone() {
		if (state.remaining)
			return;
		if (!state.closed_count)
			return;
		applyLines(node, state);
		node.querySelector('.bn_Y')?.click();
	}

	function wire() {
		const host = node.querySelector('#conf-carrier-volumes');
		if (!host || host.dataset.wired === '1')
			return;
		host.dataset.wired = '1';
		host.addEventListener('click', async (ev) => {
			try {
				if (ev.target.closest('.cv-save-carrier')) {
					const carrierId = Number(host.querySelector('#cv-carrier')?.value);
					if (!carrierId)
						return notify('Selecione o transportador.', 'Conferência de Saída');
					const saved = await api.call(api.set_output_carrier_for_volumes, ctx.companyId, ctx.outputId, carrierId);
					if (saved == null)
						return;
					state.ask_next = false;
					await refresh();
					return;
				}
				if (ev.target.closest('.cv-open')) {
					const boxId = Number(host.querySelector('#cv-box')?.value);
					if (!boxId)
						return notify('Selecione a caixa.', 'Conferência de Saída');
					const opened = await api.call(api.open_output_carrier_volume, ctx.companyId, ctx.outputId, boxId);
					if (opened == null)
						return;
					state.ask_next = false;
					await refresh();
					host.querySelector('#cv-barcode')?.focus();
					return;
				}
				if (ev.target.closest('.cv-add')) {
					await addItem();
					return;
				}
				const closeBtn = ev.target.closest('.cv-close');
				if (closeBtn) {
					const closed = await api.call(api.close_output_carrier_volume, ctx.companyId, ctx.outputId, closeBtn.dataset.id);
					if (closed == null)
						return;
					state.ask_next = !!closed.remaining;
					await refresh();
					if (!closed.remaining)
						await finishIfDone();
					return;
				}
				const reopenBtn = ev.target.closest('.cv-reopen');
				if (reopenBtn) {
					const opened = await api.call(api.reopen_output_carrier_volume, ctx.companyId, ctx.outputId, reopenBtn.dataset.id);
					if (opened == null)
						return;
					state.ask_next = false;
					if (finishButton)
						finishButton.disabled = false;
					ctx.output.status = 40;
					await refresh();
				}
			} catch (error) {
				notify(error?.message || 'Falha no volume.', 'Conferência de Saída');
			}
		});
		host.addEventListener('keydown', async (ev) => {
			if (ev.key !== 'Enter' || ev.target?.id !== 'cv-barcode')
				return;
			ev.preventDefault();
			ev.stopPropagation();
			await addItem();
		});
	}

	async function addItem() {
		const host = node.querySelector('#conf-carrier-volumes');
		const openVolume = (state.volumes || []).find((volume) => volume.status === 10);
		if (!openVolume)
			return notify('Abra um volume antes de bipar.', 'Conferência de Saída');
		const barcode = host.querySelector('#cv-barcode')?.value?.trim();
		if (!barcode)
			return;
		const rawQty = host.querySelector('#cv-qty')?.value?.trim();
		const payload = { barcode };
		if (rawQty)
			payload.quantity = Number(rawQty);
		const added = await api.call(api.add_output_carrier_volume_item, ctx.companyId, ctx.outputId, openVolume.id, payload);
		if (added == null)
			return;
		if (added.weight_alert)
			notify(`Peso acima do máximo da caixa ${openVolume.code}. O bip foi gravado.`, 'Conferência de Saída');
		await refresh();
		const barcodeInput = node.querySelector('#cv-barcode');
		if (barcodeInput) {
			barcodeInput.value = '';
			barcodeInput.focus();
		}
		const qtyInput = node.querySelector('#cv-qty');
		if (qtyInput)
			qtyInput.value = '';
	}

	await refresh();
}
